import { useState, useEffect, useMemo } from 'react';
import {
  getFirestore,
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  getDocs,
} from 'firebase/firestore';
import type {
  Rollout,
  Deployment,
  SetupTask,
  TestExecution,
  PullRequestDocument,
} from '@finvari/deliver';
import { computeStats } from '../utils/computeStats';

// --- Types ---

export interface DailyRate {
  date: string;
  total: number;
  failed: number;
  rate: number; // 0-100
}

export interface DailyStats {
  date: string;
  min: number;
  max: number;
  median: number;
  p90: number;
}

export interface MetricsData {
  loading: boolean;
  error: string | null;

  // Failure counts per day (total + failed)
  rolloutFailureRates: DailyRate[];
  deploymentFailureRates: DailyRate[];
  setupTaskFailureRates: DailyRate[];
  testFailureRates: DailyRate[];

  // Duration stats per day (in minutes, capped at 60)
  rolloutDurations: DailyStats[];
  deploymentDurations: DailyStats[];
  setupTaskDurations: DailyStats[];
  testDurations: DailyStats[];

  // Summary stat cards (row 1)
  totalRollouts: number;
  totalTestRuns: number;
  overallRolloutFailureRate: number;
  overallTestFailureRate: number;

  // PR lifecycle stats (row 2, hours for time metrics, raw count for comments)
  creationToMergeStats: Stats | null;
  deployToApprovalStats: Stats | null;
  approvalToMergeStats: Stats | null;
  commentsPerPRStats: Stats | null;
}

export interface Stats {
  min: number;
  max: number;
  median: number;
  p90: number;
}

// --- Helpers ---

const MAX_DURATION_MS = 60 * 60_000; // 1 hour cap

function dayKey(isoStr: string): string {
  const d = new Date(isoStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function weekKey(isoStr: string): string {
  const d = new Date(isoStr);
  // Roll back to Monday
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.getFullYear(), d.getMonth(), diff);
  return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
}

function msToMinutes(ms: number): number {
  return Math.min(ms, MAX_DURATION_MS) / 60_000;
}

function msToHours(ms: number): number {
  return ms / 3_600_000;
}

function groupByPeriod<T>(items: T[], getDate: (item: T) => string, periodKey: (iso: string) => string): Record<string, T[]> {
  const map: Record<string, T[]> = {};
  items.forEach((item) => {
    const key = periodKey(getDate(item));
    if (!map[key]) map[key] = [];
    map[key].push(item);
  });
  return map;
}

function failureRatesByPeriod<T>(
  items: T[],
  getDate: (item: T) => string,
  isFailed: (item: T) => boolean,
  periodKey: (iso: string) => string,
): DailyRate[] {
  const grouped = groupByPeriod(items, getDate, periodKey);
  return Object.keys(grouped)
    .map((date) => {
      const group = grouped[date];
      const failed = group.filter(isFailed).length;
      return { date, total: group.length, failed, rate: group.length > 0 ? (failed / group.length) * 100 : 0 };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

function durationStatsByPeriod<T>(
  items: T[],
  getDate: (item: T) => string,
  getDurationMs: (item: T) => number | null,
  periodKey: (iso: string) => string,
): DailyStats[] {
  const grouped = groupByPeriod(items, getDate, periodKey);
  const results: DailyStats[] = [];
  Object.keys(grouped).forEach((date) => {
    const durations = grouped[date]
      .map(getDurationMs)
      .filter((d): d is number => d !== null && d > 0)
      .map(msToMinutes);
    const stats = computeStats(durations);
    if (stats) results.push({ date, ...stats });
  });
  return results.sort((a, b) => a.date.localeCompare(b.date));
}

function overallFailureRate<T>(items: T[], isFailed: (item: T) => boolean): number {
  if (items.length === 0) return 0;
  return (items.filter(isFailed).length / items.length) * 100;
}

// --- Hook ---

export function useMetricsData(days: number): MetricsData {
  const [rollouts, setRollouts] = useState<Rollout[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [setupTasks, setSetupTasks] = useState<SetupTask[]>([]);
  const [testExecutions, setTestExecutions] = useState<TestExecution[]>([]);
  const [allPRs, setAllPRs] = useState<PullRequestDocument[]>([]);
  const [mergedPRs, setMergedPRs] = useState<PullRequestDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      const startDateStr = startDate.toISOString();
      const db = getFirestore();

      try {
        const [rolloutsSnap, deploymentsSnap, setupTasksSnap, testExecSnap, prsSnap] =
          await Promise.all([
            getDocs(query(collection(db, 'rollouts'), where('createdAt', '>=', startDateStr), orderBy('createdAt', 'desc'))),
            getDocs(query(collectionGroup(db, 'deployments'), where('createdAt', '>=', startDateStr), orderBy('createdAt', 'desc'))),
            getDocs(query(collectionGroup(db, 'setupTasks'), where('createdAt', '>=', startDateStr), orderBy('createdAt', 'desc'))),
            getDocs(query(collection(db, 'testExecutions'), where('createdAt', '>=', startDateStr), orderBy('createdAt', 'desc'))),
            getDocs(query(collection(db, 'pullRequests'))),
          ]);

        if (cancelled) return;

        setRollouts(rolloutsSnap.docs.map((d) => d.data() as Rollout));
        setDeployments(deploymentsSnap.docs.map((d) => d.data() as Deployment));
        setSetupTasks(setupTasksSnap.docs.map((d) => d.data() as SetupTask));
        setTestExecutions(testExecSnap.docs.map((d) => d.data() as TestExecution));

        const prDocs = prsSnap.docs.map((d) => d.data() as PullRequestDocument);
        // All non-main PRs for metrics that don't require merge
        setAllPRs(prDocs.filter((pr) => pr.pr.id !== 0));
        // Merged PRs for lifecycle metrics
        setMergedPRs(
          prDocs.filter((pr) => {
            const mergedAt = pr.pr.merged_at;
            return mergedAt && new Date(mergedAt) >= startDate;
          }),
        );

        setLoading(false);
      } catch (err: any) {
        if (cancelled) return;
        console.error('Error fetching metrics data:', err);
        setError(err.message || 'Failed to fetch metrics data');
        setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [days]);

  // --- Period key: day for 7/30d, week for 90d ---

  const pk = days >= 90 ? weekKey : dayKey;

  // --- Failure rates ---

  const rolloutFailureRates = useMemo(
    () => failureRatesByPeriod(rollouts, (r) => r.createdAt, (r) => r.status === 'failed', pk),
    [rollouts, pk],
  );
  const deploymentFailureRates = useMemo(
    () => failureRatesByPeriod(deployments, (d) => d.createdAt, (d) => d.status === 'failed', pk),
    [deployments, pk],
  );
  const setupTaskFailureRates = useMemo(
    () => failureRatesByPeriod(setupTasks, (s) => s.createdAt, (s) => s.status === 'failed', pk),
    [setupTasks, pk],
  );
  const testFailureRates = useMemo(
    () => failureRatesByPeriod(testExecutions, (t) => t.createdAt, (t) => t.status === 'failed', pk),
    [testExecutions, pk],
  );

  // --- Durations (capped at 60min) ---

  const rolloutDurations = useMemo(
    () => durationStatsByPeriod(
      rollouts.filter((r) => r.status === 'success' && r.finishedAt),
      (r) => r.createdAt,
      (r) => (r.finishedAt ? new Date(r.finishedAt).getTime() - new Date(r.createdAt).getTime() : null),
      pk,
    ),
    [rollouts, pk],
  );
  const deploymentDurations = useMemo(
    () => durationStatsByPeriod(
      deployments.filter((d) => d.status === 'success' && d.finishedAt),
      (d) => d.createdAt,
      (d) => (d.finishedAt ? new Date(d.finishedAt).getTime() - new Date(d.createdAt).getTime() : null),
      pk,
    ),
    [deployments, pk],
  );
  const setupTaskDurations = useMemo(
    () => durationStatsByPeriod(
      setupTasks.filter((s) => s.status === 'success' && s.finishedAt && s.startedAt),
      (s) => s.createdAt,
      (s) => (s.finishedAt && s.startedAt ? new Date(s.finishedAt).getTime() - new Date(s.startedAt).getTime() : null),
      pk,
    ),
    [setupTasks, pk],
  );
  const testDurations = useMemo(
    () => durationStatsByPeriod(
      testExecutions.filter((t) => t.status !== 'cancelled' && t.subjects.length > 0 &&
        (t.duration || (t.completedAt && t.createdAt))),
      (t) => t.createdAt,
      (t) => {
        const durationMs = t.duration ||
          (t.completedAt ? new Date(t.completedAt).getTime() - new Date(t.createdAt).getTime() : null);
        return durationMs ? durationMs / t.subjects.length : null;
      },
      pk,
    ),
    [testExecutions, pk],
  );

  // --- PR lifecycle medians ---

  const earliestRolloutByPR = useMemo(() => {
    const map: Record<number, string> = {};
    rollouts.forEach((r) => {
      if (r.prNumber && r.prNumber !== 0) {
        const existing = map[r.prNumber];
        if (!existing || r.createdAt < existing) {
          map[r.prNumber] = r.createdAt;
        }
      }
    });
    return map;
  }, [rollouts]);

  const creationToMergeStats = useMemo(() => {
    const values = mergedPRs
      .filter((pr) => pr.pr.merged_at && pr.pr.created_at)
      .map((pr) => msToHours(new Date(pr.pr.merged_at!).getTime() - new Date(pr.pr.created_at).getTime()))
      .filter((v) => v > 0);
    return computeStats(values);
  }, [mergedPRs]);

  const deployToApprovalStats = useMemo(() => {
    const values = allPRs
      .filter((pr) => {
        const deployAt = earliestRolloutByPR[pr.pr.number];
        const firstApproval = (pr.reviews || []).find((r) => r.state === 'APPROVED')?.submitted_at;
        return deployAt && firstApproval;
      })
      .map((pr) => {
        const deployAt = earliestRolloutByPR[pr.pr.number];
        const approvedReviews = (pr.reviews || [])
          .filter((r) => r.state === 'APPROVED')
          .sort((a, b) => a.submitted_at.localeCompare(b.submitted_at));
        return msToHours(new Date(approvedReviews[0].submitted_at).getTime() - new Date(deployAt).getTime());
      })
      .filter((v) => v > 0);
    return computeStats(values);
  }, [allPRs, earliestRolloutByPR]);

  const approvalToMergeStats = useMemo(() => {
    const values = mergedPRs
      .filter((pr) => {
        const firstApproval = (pr.reviews || []).find((r) => r.state === 'APPROVED')?.submitted_at;
        return pr.pr.merged_at && firstApproval;
      })
      .map((pr) => {
        const approvedReviews = (pr.reviews || [])
          .filter((r) => r.state === 'APPROVED')
          .sort((a, b) => a.submitted_at.localeCompare(b.submitted_at));
        return msToHours(new Date(pr.pr.merged_at!).getTime() - new Date(approvedReviews[0].submitted_at).getTime());
      })
      .filter((v) => v > 0);
    return computeStats(values);
  }, [mergedPRs]);

  const commentsPerPRStats = useMemo(() => {
    const values = allPRs
      .filter((pr) => pr.commentCount != null)
      .map((pr) => pr.commentCount!);
    return computeStats(values);
  }, [allPRs]);

  return {
    loading,
    error,
    rolloutFailureRates,
    deploymentFailureRates,
    setupTaskFailureRates,
    testFailureRates,
    rolloutDurations,
    deploymentDurations,
    setupTaskDurations,
    testDurations,
    totalRollouts: rollouts.length,
    totalTestRuns: testExecutions.length,
    overallRolloutFailureRate: overallFailureRate(rollouts, (r) => r.status === 'failed'),
    overallTestFailureRate: overallFailureRate(testExecutions, (t) => t.status === 'failed'),
    creationToMergeStats,
    deployToApprovalStats,
    approvalToMergeStats,
    commentsPerPRStats,
  };
}
